import {
  DateRangePickerField,
  Form,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { GlobalTagFormSchema } from '@/features/tag/models';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useTagTypeOptions } from '../hooks';

type FormValues = z.infer<typeof GlobalTagFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const GlobalTagForm: FC<Props> = ({ type, initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(GlobalTagFormSchema),
  });

  const { tagTypeOptions } = useTagTypeOptions();

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="type"
        label={t('scope-tag-type', { scope: t('global-tag') })}
        control={control}
        errors={errors}
        options={tagTypeOptions}
        required
        disabled={type === 'edit'}
      />
      <TextInputField
        name="name"
        autocompleteKey="globalTag.name"
        label={t('scope-tag-name', { scope: t('global-tag') })}
        control={control}
        errors={errors}
        required
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
